import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Checkbox, Input, message, Modal, Radio, Select, Space, Spin, Switch, Tabs, Tooltip, Typography, Upload,
} from 'antd';
import {
  AppstoreOutlined, BankOutlined, BarChartOutlined, CalendarOutlined,
  CloseOutlined, DownOutlined, FileTextOutlined, GlobalOutlined, HolderOutlined,
  LinkOutlined, LockOutlined, NotificationOutlined, PlusOutlined, RightOutlined,
  SolutionOutlined, StarOutlined, TagsOutlined, TeamOutlined, UnlockOutlined,
  UploadOutlined, UserOutlined,
} from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import TemplateEditor from '../../../components/TemplateEditor';
import {
  getAllUsers,
  getAvailableCollections,
  getArraySubfields,
  getCollectionFields,
  getModules,
  getUserId,
  getUserModuleFields,
  updateUserModuleFieldConfig,
  uploadListViewActionIcon,
  getUserPermissions,
  updateUserPermissions,
} from '../../../services/adminApi';
import { getDropdownValues } from '../../../services/dropdownApi';
import '../../../styles/UserSettingsSection.css';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_MODULE_TABS = [
  { key: 'job',         label: 'Jobs'        },
  { key: 'candidate',   label: 'Candidates'  },
  { key: 'submissions', label: 'Submissions' },
];

const CONFIG_TYPES = [
  { key: 'listView',     label: 'List View'   },
  { key: 'filter',       label: 'Filter'      },
  { key: 'form',         label: 'Form'        },
  { key: 'permissions',  label: 'Permissions' },
];

const RENDER_TYPES = [
  { key: '',       label: 'Text',   icon: <span style={{ fontSize: 14, fontWeight: 700 }}>Aa</span> },
  { key: 'badge',  label: 'Badge',  icon: <TagsOutlined style={{ fontSize: 16 }} />                  },
  { key: 'avatar', label: 'Avatar', icon: <UserOutlined style={{ fontSize: 16 }} />                  },
  { key: 'link',   label: 'Link',   icon: <LinkOutlined style={{ fontSize: 16 }} />                  },
];

function getUserDisplayName(raw) {
  return (
    raw?.USER_NAME ?? raw?.user_name ?? raw?.username ??
    raw?.name      ?? raw?.full_name ?? raw?.fullName ??
    raw?.email     ?? `User ${raw?.USER_ID ?? raw?.user_id ?? '?'}`
  );
}

function normalizeUserRecord(raw) {
  return {
    ...raw,
    key:      raw.USER_ID   ?? raw.user_id   ?? raw.userId ?? raw.id ?? raw._id,
    _name:    getUserDisplayName(raw),
    _email:   raw.email     ?? raw.EMAIL     ?? raw.user_email ?? '',
    _role:    raw.role_name ?? raw.roleName  ?? raw.ROLE_NAME  ?? '',
    _rawRef:  raw,
  };
}

const BADGE_COLORS = [
  { color: '#52c41a', bg: '#f6ffed' }, { color: '#1677ff', bg: '#e6f7ff' },
  { color: '#fa8c16', bg: '#fff7e6' }, { color: '#ff4d4f', bg: '#fff2f0' },
  { color: '#722ed1', bg: '#f9f0ff' }, { color: '#13c2c2', bg: '#e6fffb' },
  { color: '#eb2f96', bg: '#fff0f6' }, { color: '#faad14', bg: '#fffbe6' },
];

const API_MODULE_MAP = { job: 'jobs', candidate: 'candidates', submissions: 'submissions' };

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeModuleTab(module) {
  if (typeof module === 'string') {
    return { key: module, apiModule: module, label: moduleTitle(module) };
  }

  const key = (
    module?.key ?? module?.moduleKey ?? module?.value ?? module?.module ??
    module?.apiModule ?? module?.name ?? module?.modulename ?? module?.menuName
  );
  if (!key) return null;

  const apiModule = (
    module?.apiModule ?? module?.api_module ?? module?.module ?? module?.value ??
    module?.key ?? module?.name ?? module?.modulename ?? module?.menuName ?? key
  );

  return {
    key,
    apiModule,
    label: (
      module?.label ?? module?.moduleLabel ?? module?.moduleName ??
      module?.name ?? module?.modulename ?? module?.menuName ?? moduleTitle(key)
    ),
  };
}

function normalizeModuleTabs(modules) {
  const source = Array.isArray(modules) && modules.length ? modules : DEFAULT_MODULE_TABS;
  const seen = new Set();
  const tabs = source
    .map(normalizeModuleTab)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.key)) return false;
      seen.add(module.key);
      return true;
    });

  return tabs.length ? tabs : DEFAULT_MODULE_TABS;
}

// ─────────────────────────────────────────────────────────────
// List View expanded detail panel
// ─────────────────────────────────────────────────────────────
function ListViewDetails({
  field, collections, collFields, subfields,
  onCollectionChange, onLookupFieldChange,
  addSecondaryField, removeSecondaryField,
  addActionButton, updateActionButton, removeActionButton,
  moduleKey, fieldKey, update,
}) {
  const [newSecondary, setNewSecondary] = useState('');
  const [iconUploading, setIconUploading] = useState({});
  const [detecting, setDetecting] = useState(false);

  // Badge helpers — all state managed through update()
  function setBadgeMode(mode) {
    if (mode === 'common') update({ commonStyle: { color: '#1677ff', bg: '#e6f7ff', variant: 'badge' }, valueStyles: [] });
    else update({ commonStyle: null });
  }
  function updateCommonStyle(patch) {
    update({ commonStyle: { ...(field.commonStyle ?? { color: '#595959', bg: '#f5f5f5', variant: 'badge' }), ...patch } });
  }
  function addValueStyle() {
    update({ valueStyles: [...(field.valueStyles ?? []), { value: '', label: '', color: '#595959', bg: '#f5f5f5', variant: 'badge' }] });
  }
  function updateValueStyle(idx, patch) {
    const styles = [...(field.valueStyles ?? [])];
    styles[idx] = { ...styles[idx], ...patch };
    update({ valueStyles: styles });
  }
  function removeValueStyle(idx) {
    update({ valueStyles: (field.valueStyles ?? []).filter((_, i) => i !== idx) });
  }
  async function autoDetectValues() {
    setDetecting(true);
    try {
      const apiMod = field.apiModule ?? API_MODULE_MAP[moduleKey] ?? moduleKey;
      const result = await getDropdownValues(apiMod, fieldKey, '', 50, 0);
      const items = Array.isArray(result) ? result : (result?.data ?? []);
      const styles = items.map((item, i) => {
        const val = typeof item === 'object' ? (item.value ?? item.label ?? '') : String(item);
        const lbl = typeof item === 'object' ? (item.label ?? item.value ?? '') : String(item);
        return { value: String(val), label: String(lbl), variant: 'badge', ...BADGE_COLORS[i % BADGE_COLORS.length] };
      });
      if (styles.length === 0) { message.info('No values found for this field'); return; }
      update({ valueStyles: styles });
    } catch { message.error('Could not detect values'); }
    finally { setDetecting(false); }
  }

  async function handleIconUpload(btnIdx, file) {
    setIconUploading((p) => ({ ...p, [btnIdx]: true }));
    try {
      const result = await uploadListViewActionIcon(file);
      updateActionButton(btnIdx, {
        iconUrl:  result?.iconUrl  ?? result?.url ?? '',
        iconPath: result?.iconPath ?? result?.path ?? '',
      });
    } catch { message.error('Icon upload failed'); }
    finally { setIconUploading((p) => ({ ...p, [btnIdx]: false })); }
    return false;
  }

  const renderType = field.renderType ?? '';
  const isCommon = Boolean(field.commonStyle);
  const cs = field.commonStyle ?? {};
  const vs = field.valueStyles ?? [];
  const rc = field.renderConfig ?? {};

  return (
    <>
      <div className="uss-detail-section">
        <div className="uss-detail-label">Display Style</div>
        <div className="uss-detail-row">
          {RENDER_TYPES.map((rt) => (
            <button key={rt.key}
              className={`uss-render-type-btn${renderType === rt.key ? ' active' : ''}`}
              onClick={() => update({ renderType: rt.key, commonStyle: null, valueStyles: [], renderConfig: null })}>
              {rt.icon}<span>{rt.label}</span>
            </button>
          ))}
        </div>

        {/* ── Badge color config ── */}
        {renderType === 'badge' && (
          <div className="uss-badge-config">
            <Radio.Group size="small" value={isCommon ? 'common' : 'perValue'}
              onChange={(e) => setBadgeMode(e.target.value)} style={{ marginBottom: 8 }}>
              <Radio value="perValue">Per value</Radio>
              <Radio value="common">Same for all</Radio>
            </Radio.Group>

            {isCommon && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Tooltip title="Text color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="color" value={cs.color || '#595959'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                      onChange={(e) => updateCommonStyle({ color: e.target.value })} />
                    <Text style={{ fontSize: 10 }}>Text</Text>
                  </div>
                </Tooltip>
                <Tooltip title="Background color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="color" value={cs.bg || '#f5f5f5'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                      onChange={(e) => updateCommonStyle({ bg: e.target.value })} />
                    <Text style={{ fontSize: 10 }}>BG</Text>
                  </div>
                </Tooltip>
                <Select size="small" style={{ width: 80 }} value={cs.variant || 'badge'}
                  options={[{ value: 'badge', label: 'Badge' }, { value: 'dot', label: 'Dot' }, { value: 'tag', label: 'Tag' }]}
                  onChange={(v) => updateCommonStyle({ variant: v })} />
                <span style={{ padding: '1px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, color: cs.color || '#595959', background: cs.bg || '#f5f5f5' }}>
                  Preview
                </span>
              </div>
            )}

            {!isCommon && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text style={{ fontSize: 11, color: '#555', fontWeight: 600 }}>Value → Style mapping</Text>
                  <Space size={6}>
                    <Button size="small" loading={detecting} onClick={autoDetectValues}>Auto-detect values</Button>
                    <Button size="small" icon={<PlusOutlined />} onClick={addValueStyle}>Add</Button>
                  </Space>
                </div>
                {vs.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>Click "Auto-detect values" to populate from live data, or "Add" to map manually.</Text>
                )}
                {vs.map((style, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 62px 62px 54px 26px', gap: 5, alignItems: 'center', marginBottom: 6 }}>
                    <Input size="small" placeholder="raw value" value={style.value ?? ''}
                      onChange={(e) => updateValueStyle(idx, { value: e.target.value })} />
                    <Input size="small" placeholder="display label" value={style.label ?? ''}
                      onChange={(e) => updateValueStyle(idx, { label: e.target.value })} />
                    <Tooltip title="Text color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <input type="color" value={style.color || '#595959'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                          onChange={(e) => updateValueStyle(idx, { color: e.target.value })} />
                        <Text style={{ fontSize: 10 }}>Text</Text>
                      </div>
                    </Tooltip>
                    <Tooltip title="Background color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <input type="color" value={style.bg || '#f5f5f5'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                          onChange={(e) => updateValueStyle(idx, { bg: e.target.value })} />
                        <Text style={{ fontSize: 10 }}>BG</Text>
                      </div>
                    </Tooltip>
                    <span style={{ padding: '1px 6px', borderRadius: 20, fontSize: 11, fontWeight: 500, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: style.color || '#595959', background: style.bg || '#f5f5f5' }}>
                      {style.label || style.value || '…'}
                    </span>
                    <Button size="small" type="text" danger icon={<CloseOutlined />} onClick={() => removeValueStyle(idx)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Avatar config ── */}
        {renderType === 'avatar' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8, marginTop: 8 }}>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Name field</Text>
              <Select size="small" showSearch allowClear style={{ width: '100%' }}
                placeholder="e.g. recruiterName" value={rc.labelField || undefined}
                options={subfields.length > 0 ? subfields.map((sf) => ({ value: sf, label: sf })) : []}
                onChange={(v) => update({ renderConfig: { ...rc, labelField: v ?? '' } })} />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Color field (opt.)</Text>
              <Select size="small" showSearch allowClear style={{ width: '100%' }}
                placeholder="color" value={rc.colorField || undefined}
                options={subfields.length > 0 ? subfields.map((sf) => ({ value: sf, label: sf })) : []}
                onChange={(v) => update({ renderConfig: { ...rc, colorField: v ?? '' } })} />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Max visible</Text>
              <Input size="small" type="number" min={1} max={10} value={rc.maxVisible || 3}
                onChange={(e) => update({ renderConfig: { ...rc, maxVisible: Number(e.target.value) || 3 } })} />
            </div>
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Display Template</div>
        <TemplateEditor value={field.displayTemplate ?? ''}
          onChange={(v) => update({ displayTemplate: v })}
          placeholder="e.g. {{firstName}} {{lastName}}" />
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Link
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.isLink)}
            onChange={(v) => update({ isLink: v })} />
        </div>
        {field.isLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
            <TemplateEditor value={field.linkTemplate ?? ''} onChange={(v) => update({ linkTemplate: v })}
              placeholder="e.g. /jobs/{{jobId}}" />
            <div className="uss-detail-row">
              <Select size="small" value={field.linkType ?? 'internal'} onChange={(v) => update({ linkType: v })} style={{ width: 120 }}>
                <Option value="internal">internal</Option>
                <Option value="external">external</Option>
              </Select>
              <Select size="small" value={field.linkTarget ?? '_self'} onChange={(v) => update({ linkTarget: v })} style={{ width: 100 }}>
                <Option value="_self">_self</Option>
                <Option value="_blank">_blank</Option>
              </Select>
            </div>
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Secondary Fields</div>
        <div className="uss-detail-row" style={{ flexWrap: 'wrap' }}>
          {(field.secondaryFields ?? []).map((f) => (
            <span key={f} className="uss-secondary-tag">
              {f}
              <button onClick={() => removeSecondaryField(f)}>×</button>
            </span>
          ))}
        </div>
        <div className="uss-detail-row">
          <Input size="small" placeholder="Add field key" value={newSecondary}
            onChange={(e) => setNewSecondary(e.target.value)}
            onPressEnter={() => { addSecondaryField(newSecondary); setNewSecondary(''); }}
            style={{ width: 160 }} />
          <Button size="small" type="link" icon={<PlusOutlined />}
            onClick={() => { addSecondaryField(newSecondary); setNewSecondary(''); }}>Add</Button>
        </div>
        {(field.secondaryFields?.length > 0) && (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Separator:</Text>
            <Input size="small" value={field.secondarySeparator ?? ''} placeholder="·" style={{ width: 64 }}
              onChange={(e) => update({ secondarySeparator: e.target.value })} />
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Lookup (FK resolve)
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.lookup)}
            onChange={(v) => update({ lookup: v ? { collection: '', displayField: '', valueField: '', projectFields: [] } : null })} />
        </div>
        {field.lookup && (
          <div className="uss-lookup-grid">
            <div className="uss-lookup-row">
              <span className="uss-lookup-label">Collection</span>
              <Select size="small" value={field.lookup?.collection ?? ''} onChange={onCollectionChange}
                showSearch placeholder="Select collection" style={{ flex: 1 }}>
                {collections.map((c) => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </div>
            {collFields.length > 0 && (
              <>
                <div className="uss-lookup-row">
                  <span className="uss-lookup-label">Value Field</span>
                  <Select size="small" value={field.lookup?.valueField ?? ''}
                    onChange={(v) => onLookupFieldChange(field.fieldKey, { valueField: v })}
                    placeholder="ID field" style={{ flex: 1 }}>
                    {collFields.map((f) => <Option key={f} value={f}>{f}</Option>)}
                  </Select>
                </div>
                <div className="uss-lookup-row">
                  <span className="uss-lookup-label">Display Field</span>
                  <Select size="small" value={field.lookup?.displayField ?? ''}
                    onChange={(v) => onLookupFieldChange(field.fieldKey, { displayField: v })}
                    placeholder="Label field" style={{ flex: 1 }}>
                    {[...collFields, ...subfields].map((f) => <Option key={f} value={f}>{f}</Option>)}
                  </Select>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Action Buttons <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-muted)' }}>(max 3)</span></span>
          {(field.actionButtons ?? []).length < 3 && (
            <Button type="link" size="small" icon={<PlusOutlined />} onClick={addActionButton}>Add Icon</Button>
          )}
        </div>
        {(field.actionButtons ?? []).map((btn, i) => (
          <div key={i}>
            <div className="uss-action-btn-row">
              <Upload showUploadList={false} beforeUpload={(f) => { handleIconUpload(i, f); return false; }} accept="image/*">
                <Tooltip title="Click to upload icon">
                  <div className="uss-action-btn-icon-slot">
                    {iconUploading[i]
                      ? <Spin size="small" />
                      : btn.iconUrl
                        ? <img src={btn.iconUrl} alt={`action-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <UploadOutlined style={{ color: '#aaa', fontSize: 14 }} />
                    }
                  </div>
                </Tooltip>
              </Upload>
              <Input size="small" value={btn.name ?? ''} onChange={(e) => updateActionButton(i, { name: e.target.value })}
                placeholder="Tooltip (e.g. Edit)" style={{ width: 110 }} />
              <Radio.Group size="small" value={btn.type ?? 'page'} onChange={(e) => updateActionButton(i, { type: e.target.value })}>
                <Radio value="page">Page</Radio>
                <Radio value="popup">Popup</Radio>
              </Radio.Group>
              <Input size="small" value={btn.routeUrl ?? ''} onChange={(e) => updateActionButton(i, { routeUrl: e.target.value })}
                placeholder="Route URL (e.g. /jobs/:_id)" prefix={<LinkOutlined style={{ color: '#bbb' }} />} style={{ flex: 1 }} />
              <Button size="small" danger type="text" onClick={() => removeActionButton(i)}>✕</Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 36 }}>
              <Text style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>Permission:</Text>
              <Select size="small" allowClear value={btn.permissionKey || undefined}
                onChange={(v) => updateActionButton(i, { permissionKey: v ?? '' })}
                placeholder="Always visible" style={{ width: 180 }}
                options={[{ value: 'edit' }, { value: 'delete' }, { value: 'add' }, { value: 'view' }]}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 36, marginTop: 4 }}>
              <Text style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>Show only if:</Text>
              <Input size="small" value={btn.showIfField ?? ''}
                onChange={(e) => updateActionButton(i, { showIfField: e.target.value })}
                placeholder="field (e.g. internalSubmissionStatus)" style={{ width: 210 }} />
              <Text style={{ fontSize: 11, color: '#999' }}>=</Text>
              <Input size="small" value={btn.showIfValue ?? ''}
                onChange={(e) => updateActionButton(i, { showIfValue: e.target.value })}
                placeholder="value (e.g. Resubmitted)" style={{ width: 150 }} />
              {btn.showIfField && (
                <Button size="small" type="text" danger icon={<CloseOutlined />}
                  onClick={() => updateActionButton(i, { showIfField: '', showIfValue: '' })} />
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const FILTER_DROPDOWN_SOURCES = [
  { label: 'Module data (distinct)', value: 'module' },
  { label: 'Masters collection',     value: 'master' },
  { label: 'Group value',            value: 'group'  },
];

const DROPDOWN_FIELD_TYPES = ['select', 'radio', 'checkbox'];

const FILTER_INPUT_TYPE_OPTIONS = [
  { label: 'Dropdown',     value: 'dropdown'     },
  { label: 'Checkbox',     value: 'checkbox'     },
  { label: 'Radio',        value: 'radio'        },
  { label: 'Date Range',   value: 'datepicker'   },
  { label: 'Number Range', value: 'number-range' },
];

function FormFilterDetails({ field, update, configType, collections, collFields, onCollectionChange, onLookupFieldChange }) {
  const isDropdownType = DROPDOWN_FIELD_TYPES.includes(field.type);
  const hasLookup = Boolean(field.lookup);

  function handleLookupToggle(v) {
    update({ lookup: v ? { collection: '', displayField: '', valueField: '', projectFields: [] } : null });
  }

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 12 }}>Editable</Text>
        <Switch size="small" checked={Boolean(field.isEditable)} onChange={(v) => update({ isEditable: v })} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 12 }}>Mandatory</Text>
        <Switch size="small" checked={Boolean(field.ismandatory)} onChange={(v) => update({ ismandatory: v })} />
      </div>
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Value Input</Text>
          <Select
            size="small"
            value={field.filterInputType ?? 'dropdown'}
            onChange={(v) => update({ filterInputType: v })}
            options={FILTER_INPUT_TYPE_OPTIONS}
            style={{ width: 140 }}
          />
        </div>
      )}
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Default</Text>
          <Switch size="small" checked={Boolean(field.isDefaultFilter)} onChange={(v) => update({ isDefaultFilter: v })} />
        </div>
      )}
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Lookup</Text>
          <Switch size="small" checked={hasLookup} onChange={handleLookupToggle} />
        </div>
      )}
      {configType === 'filter' && hasLookup && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={field.lookup?.collection || undefined}
            onChange={onCollectionChange}
            showSearch
            placeholder="Collection"
            style={{ width: 160 }}
            options={(collections ?? []).map((c) => ({ label: c, value: c }))}
          />
          {(collFields ?? []).length > 0 && (
            <>
              <Select
                size="small"
                value={field.lookup?.valueField || undefined}
                onChange={(v) => onLookupFieldChange(field.fieldKey, { valueField: v })}
                showSearch
                placeholder="Value field (ID)"
                style={{ width: 160 }}
                options={(collFields ?? []).map((f) => ({ label: f, value: f }))}
              />
              <Input
                size="small"
                value={field.lookup?.displayField ?? ''}
                onChange={(e) => onLookupFieldChange(field.fieldKey, { displayField: e.target.value })}
                placeholder="Display field (e.g. name)"
                style={{ width: 180 }}
              />
            </>
          )}
        </div>
      )}
      {isDropdownType && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 12 }}>Source</Text>
            <Select
              size="small"
              value={field.dataSource ?? 'module'}
              onChange={(v) => update({ dataSource: v, groupName: '' })}
              options={FILTER_DROPDOWN_SOURCES}
              style={{ width: 170 }}
            />
          </div>
          {(field.dataSource ?? 'module') === 'group' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 12 }}>Group Name</Text>
              <Input
                size="small"
                value={field.groupName ?? ''}
                onChange={(e) => update({ groupName: e.target.value })}
                placeholder="e.g. skills"
                style={{ width: 120 }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Field row inside config modal
// ─────────────────────────────────────────────────────────────
function FieldRow({ field, index, configType, moduleKey, onUpdate, onReorder, collections, onFetchCollFields, dragFromRef }) {
  const [expanded, setExpanded]     = useState(false);
  const [labelEdit, setLabelEdit]   = useState(false);
  const [labelVal, setLabelVal]     = useState(field.fieldName);
  const [collFields, setCollFields] = useState([]);
  const [subfields, setSubfields]   = useState([]);
  const [over, setOver]         = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => { setLabelVal(field.fieldName); }, [field.fieldName]);

  function handleDragStart(e) { dragFromRef.current = index; e.dataTransfer.effectAllowed = 'move'; setDragging(true); }
  function handleDragEnd()    { setDragging(false); setOver(false); }
  function handleDragOver(e)  { e.preventDefault(); setOver(true); }
  function handleDragLeave()  { setOver(false); }
  function handleDrop(e) {
    e.preventDefault(); setOver(false);
    if (dragFromRef.current !== null && dragFromRef.current !== index) onReorder(dragFromRef.current, index);
    dragFromRef.current = null;
  }

  function update(patch) { onUpdate(index, patch); }

  function commitLabel() {
    setLabelEdit(false);
    const trimmed = labelVal.trim();
    if (trimmed && trimmed !== field.fieldName) update({ fieldName: trimmed });
    else setLabelVal(field.fieldName);
  }

  async function onCollectionChange(col) {
    update({ lookup: { ...field.lookup, collection: col, displayField: '', valueField: '', projectFields: [] } });
    const f = await onFetchCollFields(col);
    setCollFields(f);
  }

  async function onLookupFieldChange(fKey, patch) {
    update({ lookup: { ...field.lookup, ...patch } });
    if (patch.valueField) {
      try { setSubfields(await getArraySubfields(moduleKey, fKey)); } catch { setSubfields([]); }
    }
  }

  function addSecondaryField(val) {
    const arr = field.secondaryFields ?? [];
    if (!val || arr.includes(val)) return;
    update({ secondaryFields: [...arr, val] });
  }
  function removeSecondaryField(v) { update({ secondaryFields: (field.secondaryFields ?? []).filter((x) => x !== v) }); }

  function addActionButton() {
    const btns = field.actionButtons ?? [];
    if (btns.length >= 3) return;
    update({ actionButtons: [...btns, { name: '', iconUrl: '', iconPath: '', type: 'page', routeUrl: '', permissionKey: '' }] });
  }
  function updateActionButton(i, patch) {
    const btns = [...(field.actionButtons ?? [])];
    btns[i] = { ...btns[i], ...patch };
    update({ actionButtons: btns });
  }
  function removeActionButton(i) {
    const btns = [...(field.actionButtons ?? [])];
    btns.splice(i, 1);
    update({ actionButtons: btns });
  }

  return (
    <>
      <div
        draggable
        onDragStart={handleDragStart} onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}   onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`uss-field-row${over ? ' drag-over' : ''}${dragging ? ' dragging' : ''}`}
        style={{ gridTemplateColumns: '24px 1fr 80px 56px' }}
      >
        <span className="uss-drag-handle"><HolderOutlined /></span>

        <span className="uss-field-name">
          {field.isVirtual && <span className="uss-virtual-tag">virtual</span>}
          {labelEdit ? (
            <input autoFocus value={labelVal}
              onChange={(e) => setLabelVal(e.target.value)} onBlur={commitLabel}
              onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setLabelEdit(false); setLabelVal(field.fieldName); } }}
              onClick={(e) => e.stopPropagation()} />
          ) : (
            <Tooltip title="Double-click to rename">
              <span style={{ cursor: 'text', borderBottom: '1px dashed var(--color-border-muted)' }}
                onDoubleClick={(e) => { e.stopPropagation(); setLabelEdit(true); }}>
                {field.fieldName}
              </span>
            </Tooltip>
          )}
          <span className="uss-field-key">{field.fieldKey}</span>
        </span>

        <span className="uss-toggle-cell">
          <Switch size="small" checked={field.isVisible} onChange={(v) => update({ isVisible: v })} />
        </span>

        <span className="uss-toggle-cell">
          <Button type="text" size="small"
            icon={expanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setExpanded((p) => !p)}
            style={{ color: 'var(--color-text-muted)' }} />
        </span>
      </div>

      {expanded && (
        <div className="uss-detail-panel">
          {configType === 'listView' ? (
            <ListViewDetails
              field={field} collections={collections}
              collFields={collFields} subfields={subfields}
              onCollectionChange={onCollectionChange}
              onLookupFieldChange={onLookupFieldChange}
              addSecondaryField={addSecondaryField}
              removeSecondaryField={removeSecondaryField}
              addActionButton={addActionButton}
              updateActionButton={updateActionButton}
              removeActionButton={removeActionButton}
              moduleKey={moduleKey} fieldKey={field.fieldKey}
              update={update}
            />
          ) : (
            <FormFilterDetails
              field={field}
              update={update}
              configType={configType}
              collections={collections}
              collFields={collFields}
              onCollectionChange={onCollectionChange}
              onLookupFieldChange={onLookupFieldChange}
            />
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// User permissions panel  (matches roles-add-v2 design)
// ─────────────────────────────────────────────────────────────

const PERM_MODULE_META = {
  jobs:       { title: 'Jobs',        description: 'Manage and configure job postings.',    Icon: SolutionOutlined,    order: ['view','my_jobs','all_jobs','add','edit','delete','sourcing','assign','share'] },
  job:        { title: 'Job',         description: 'Individual job management.',             Icon: SolutionOutlined,    order: ['view','my_jobs','all_jobs','add','edit','delete','sourcing','assign','share'] },
  candidates: { title: 'Candidates',  description: 'Manage candidate profiles and data.',   Icon: UserOutlined,        order: ['view','candidate_self','candidate_all','add','edit','delete','share'] },
  submissions:{ title: 'Submissions', description: 'Review and manage submissions.',         Icon: FileTextOutlined,    order: ['view','approve','share','edit','change_status','move_onboard'] },
  vendors:    { title: 'Vendors',     description: 'Manage vendor records and details.',     Icon: BankOutlined,        order: ['view','add','edit','delete'] },
  hotlist:    { title: 'Hotlist',     description: 'Manage hotlist candidates.',             Icon: StarOutlined,        order: ['view','add','edit','delete'] },
  campaingns: { title: 'Campaigns',   description: 'Manage campaigns.',                     Icon: NotificationOutlined,order: ['view','add','edit','delete'] },
  network:    { title: 'Network',     description: 'Manage your network.',                  Icon: GlobalOutlined,      order: ['view','add','edit','delete'] },
  scheduler:  { title: 'Scheduler',   description: 'Manage scheduling.',                   Icon: CalendarOutlined,    order: ['view','add','edit','delete'] },
  reports:    { title: 'Reports',     description: 'View and manage reports.',              Icon: BarChartOutlined,    order: ['view','add','edit','delete'] },
  onboarding: { title: 'Onboarding',  description: 'Manage onboarding.',                   Icon: TeamOutlined,        order: ['view','add','edit','delete'] },
};

const PERM_LABELS = {
  view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete',
  sourcing: 'Source', assign: 'Assign', share: 'Share',
  approve: 'Approve', change_status: 'Change Status', move_onboard: 'Move to Onboard',
  candidate_self: 'Self', candidate_all: 'All Candidates',
  my_jobs: 'My Jobs', all_jobs: 'All Jobs',
};

const PERM_VIEW_SCOPE = {
  candidates: { title: 'Candidate View Access', keys: ['candidate_self', 'candidate_all'], control: 'radio'  },
  jobs:       { title: 'Job View Access',        keys: ['my_jobs', 'all_jobs'],            control: 'mixed'  },
  job:        { title: 'Job View Access',        keys: ['my_jobs', 'all_jobs'],            control: 'mixed'  },
};

function permToTitle(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

function getModuleMeta(name) {
  return PERM_MODULE_META[name] ?? {
    title: permToTitle(name),
    description: `Manage ${permToTitle(name).toLowerCase()} permissions.`,
    Icon: AppstoreOutlined,
    order: ['view', 'add', 'edit', 'delete'],
  };
}

function getPermKeys(moduleName, moduleData) {
  const available = Object.keys(moduleData?.permissions ?? {});
  const preferred = getModuleMeta(moduleName).order;
  return [...preferred.filter((k) => available.includes(k)), ...available.filter((k) => !preferred.includes(k))];
}

function getViewScopeKeys(moduleName, moduleData) {
  const config = PERM_VIEW_SCOPE[moduleName];
  if (!config) return [];
  return config.keys.filter((k) => Boolean(moduleData?.permissions?.[k]));
}

function getPrimaryKeys(moduleName, moduleData) {
  const scopeKeys = getViewScopeKeys(moduleName, moduleData);
  return getPermKeys(moduleName, moduleData).filter((k) => !scopeKeys.includes(k));
}

function getSelectableKeys(moduleName, moduleData) {
  const all = getPermKeys(moduleName, moduleData);
  if (moduleName === 'candidates') return all.filter((k) => k !== 'candidate_self');
  if (moduleName === 'jobs' || moduleName === 'job') return all.filter((k) => k !== 'my_jobs');
  return all;
}

function UserPermPanel({ user }) {
  const [roleData, setRoleData] = useState({});
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [dirty,    setDirty]    = useState(false);

  const userId = getUserId(user);

  useEffect(() => {
    if (!userId) {
      console.warn('[UserPermPanel] Could not resolve userId from user object:', user);
      return;
    }
    setLoading(true);
    setDirty(false);
    getUserPermissions(userId)
      .then((d) => setRoleData(d ?? {}))
      .catch((err) => {
        console.error('[UserPermPanel] getUserPermissions error:', err);
        message.error('Failed to load permissions');
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const moduleEntries = useMemo(
    () => Object.entries(roleData).filter(
      ([, v]) => v && typeof v.permissions === 'object' && Object.keys(v.permissions).length > 0
    ),
    [roleData]
  );

  function updatePermission(moduleName, permKey, checked) {
    setDirty(true);
    setRoleData((prev) => {
      const mod   = prev[moduleName] ?? {};
      const perms = { ...(mod.permissions ?? {}) };
      perms[permKey] = { ...perms[permKey], value: checked ? '1' : '0' };

      const scopeKeys = getViewScopeKeys(moduleName, mod);
      if (permKey === 'view' && (moduleName === 'jobs' || moduleName === 'job') && checked) {
        scopeKeys.forEach((k) => { perms[k] = { ...perms[k], value: '1' }; });
      }
      if (permKey === 'view' && !checked) {
        scopeKeys.forEach((k) => { perms[k] = { ...perms[k], value: '0' }; });
      }
      if (scopeKeys.includes(permKey) && checked && perms.view) {
        perms.view = { ...perms.view, value: '1' };
      }
      return { ...prev, [moduleName]: { ...mod, permissions: perms } };
    });
  }

  function updateViewScope(moduleName, permKey, checked) {
    setDirty(true);
    setRoleData((prev) => {
      const mod   = prev[moduleName] ?? {};
      const perms = { ...(mod.permissions ?? {}) };
      const scopeKeys = getViewScopeKeys(moduleName, mod);

      if (moduleName === 'candidates') {
        scopeKeys.forEach((k) => { perms[k] = { ...perms[k], value: k === permKey ? '1' : '0' }; });
      } else {
        if (permKey === 'my_jobs') return prev;
        perms[permKey] = { ...perms[permKey], value: checked ? '1' : '0' };
      }
      if (perms.view) perms.view = { ...perms.view, value: '1' };
      return { ...prev, [moduleName]: { ...mod, permissions: perms } };
    });
  }

  function updateAll(moduleName, checked) {
    setDirty(true);
    setRoleData((prev) => {
      const mod   = prev[moduleName] ?? {};
      const perms = Object.fromEntries(
        Object.entries(mod.permissions ?? {}).map(([k, e]) => {
          if (moduleName === 'candidates' && checked) {
            if (k === 'candidate_self') return [k, { ...e, value: '0' }];
            if (k === 'candidate_all') return [k, { ...e, value: '1' }];
          }
          if ((moduleName === 'jobs' || moduleName === 'job') && checked) {
            if (k === 'my_jobs' || k === 'all_jobs') return [k, { ...e, value: '1' }];
          }
          return [k, { ...e, value: checked ? '1' : '0' }];
        })
      );
      return { ...prev, [moduleName]: { ...mod, permissions: perms } };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateUserPermissions(userId, roleData);
      setDirty(false);
      message.success('Permissions saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Spin spinning={loading}>
      <div className="uss-perm-header">
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          <LockOutlined style={{ color: 'var(--color-brand)', marginRight: 6 }} />
          Configure module-level permissions for this user.
        </span>
        <Button
          type="primary" size="small" icon={<UnlockOutlined />}
          loading={saving} disabled={!dirty} onClick={handleSave}
        >
          Save Changes
        </Button>
      </div>

      {moduleEntries.length === 0 && !loading ? (
        <div className="uss-empty">No permission entries found for this user.</div>
      ) : (
        <div className="uperm-list">
          {moduleEntries.map(([moduleName, moduleData]) => {
            const meta          = getModuleMeta(moduleName);
            const { Icon }      = meta;
            const primaryKeys   = getPrimaryKeys(moduleName, moduleData);
            const selectableKeys= getSelectableKeys(moduleName, moduleData);
            const scopeKeys     = getViewScopeKeys(moduleName, moduleData);
            const scopeConfig   = PERM_VIEW_SCOPE[moduleName];
            const isViewEnabled = moduleData.permissions?.view?.value === '1';
            const selectedScope = scopeKeys.find((k) => moduleData.permissions[k]?.value === '1');
            const allSelected   = selectableKeys.every((k) => moduleData.permissions[k]?.value === '1');

            return (
              <section className="uperm-card" key={moduleName}>
                <div className="uperm-card-info">
                  <div className="uperm-module-icon">
                    <Icon style={{ fontSize: 22 }} />
                  </div>
                  <div>
                    <div className="uperm-module-title">{meta.title}</div>
                    <div className="uperm-module-desc">{meta.description}</div>
                  </div>
                </div>

                <div className="uperm-permissions">
                  <Checkbox
                    className="uperm-select-all"
                    checked={allSelected}
                    onChange={(e) => updateAll(moduleName, e.target.checked)}
                  >
                    Select all
                  </Checkbox>

                  <div className="uperm-chip-row">
                    {primaryKeys.map((permKey) => (
                      <label className="uperm-chip" key={permKey}>
                        <Checkbox
                          checked={moduleData.permissions[permKey]?.value === '1'}
                          onChange={(e) => updatePermission(moduleName, permKey, e.target.checked)}
                        />
                        <span>{PERM_LABELS[permKey] ?? permToTitle(permKey)}</span>
                      </label>
                    ))}
                  </div>

                  {scopeKeys.length > 0 && (
                    <div className={`uperm-view-scope${isViewEnabled ? '' : ' is-disabled'}`}>
                      <div className="uperm-scope-title">{scopeConfig.title}</div>
                      {scopeConfig.control === 'radio' ? (
                        <Radio.Group
                          className="uperm-scope-row"
                          value={selectedScope}
                          disabled={!isViewEnabled}
                          onChange={(e) => updateViewScope(moduleName, e.target.value, true)}
                        >
                          {scopeKeys.map((k) => (
                            <Radio className="uperm-scope-option" key={k} value={k}>
                              {PERM_LABELS[k] ?? permToTitle(k)}
                            </Radio>
                          ))}
                        </Radio.Group>
                      ) : (
                        <div className="uperm-scope-row">
                          {scopeKeys.map((k) => (
                            <label className="uperm-scope-option" key={k}>
                              <Checkbox
                                checked={moduleData.permissions[k]?.value === '1'}
                                disabled={!isViewEnabled || k === 'my_jobs'}
                                onChange={(e) => updateViewScope(moduleName, k, e.target.checked)}
                              />
                              <span>{PERM_LABELS[k] ?? permToTitle(k)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </Spin>
  );
}

// ─────────────────────────────────────────────────────────────
// Module field config (modal body)
// ─────────────────────────────────────────────────────────────
function ModuleFieldConfig({ user, configType }) {
  const [moduleFields, setModuleFields] = useState({});
  const [moduleTabs, setModuleTabs]     = useState(DEFAULT_MODULE_TABS);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState({});
  const [activeModule, setActiveModule] = useState('job');
  const [collections, setCollections]   = useState([]);
  const collFieldsCache = useRef({});
  const dragFromRef     = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [rawModules, colls] = await Promise.all([
          getModules().catch(() => []),
          getAvailableCollections(),
        ]);
        if (cancelled) return;

        const tabs = normalizeModuleTabs(rawModules);
        setModuleTabs(tabs);
        setCollections(colls);
        setActiveModule((current) => (
          tabs.some((tab) => tab.key === current) ? current : tabs[0]?.key ?? ''
        ));

        const fields = await getUserModuleFields(user, configType, tabs);
        if (!cancelled) setModuleFields(fields);
      } catch {
        if (!cancelled) message.error('Failed to load field config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, configType]);

  const fields = moduleFields[activeModule] ?? [];

  function setFields(module, updater) {
    setModuleFields((prev) => ({
      ...prev,
      [module]: typeof updater === 'function' ? updater(prev[module] ?? []) : updater,
    }));
  }

  function handleUpdate(idx, patch) {
    setFields(activeModule, (prev) => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }

  function handleReorder(from, to) {
    setFields(activeModule, (prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr.map((f, i) => ({ ...f, order: i }));
    });
  }

  async function fetchCollFields(collection) {
    if (collFieldsCache.current[collection]) return collFieldsCache.current[collection];
    try {
      const f = await getCollectionFields(collection);
      collFieldsCache.current[collection] = f;
      return f;
    } catch { return []; }
  }

  async function handleSave() {
    const mod = activeModule;
    setSaving((p) => ({ ...p, [mod]: true }));
    try {
      await updateUserModuleFieldConfig(user, mod, fields, configType, moduleTabs);
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving((p) => ({ ...p, [mod]: false }));
    }
  }

  return (
    <Spin spinning={loading}>
      <Tabs size="small" activeKey={activeModule} onChange={setActiveModule}
        items={moduleTabs.map((t) => ({ key: t.key, label: t.label }))} />
      <div className="uss-field-header" style={{ gridTemplateColumns: '24px 1fr 80px 56px' }}>
        <span /><span>Field</span>
        <span style={{ textAlign: 'center' }}>Visible</span>
        <span />
      </div>
      <div className="uss-field-list">
        {fields.length === 0 && !loading && <div className="uss-empty">No fields found.</div>}
        {fields.map((field, idx) => (
          <FieldRow key={`${field.fieldKey ?? idx}-${activeModule}`}
            field={field} index={idx}
            configType={configType} moduleKey={activeModule}
            onUpdate={handleUpdate} onReorder={handleReorder}
            collections={collections} onFetchCollFields={fetchCollFields}
            dragFromRef={dragFromRef}
          />
        ))}
      </div>
      <div className="uss-modal-footer">
        <Button type="primary" size="small" loading={saving[activeModule]} onClick={handleSave}>
          Save {moduleTabs.find((t) => t.key === activeModule)?.label}
        </Button>
      </div>
    </Spin>
  );
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function UsersSection() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);
  const [configType, setConfigType]     = useState('listView');
  const [users, setUsers]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(10);
  const [searchQuery, setSearchQuery]   = useState('');

  const fetchUsers = useCallback(async ({ offset, limit, searchQuery: sq } = {}) => {
    setLoading(true);
    try {
      const result = await getAllUsers({ offset, limit, searchQuery: sq ?? '' });
      const normalized = (result.users ?? []).map(normalizeUserRecord);
      setUsers(normalized);
      setTotal(result.total ?? normalized.length);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers({ offset: (page - 1) * pageSize, limit: pageSize, searchQuery });
  }, [fetchUsers, page, pageSize, searchQuery]);

  function openConfig(record, tab = 'listView') {
    setSelectedUser({ ...record._rawRef, key: record.key });
    setConfigType(tab);
    setModalOpen(true);
  }

  const fields = [
    {
      label: 'Name', value: '_name', width: 220,
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          {record._email && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{record._email}</div>}
        </div>
      ),
    },
    { label: 'Role',  value: '_role',  width: 180 },
    {
      label: 'Actions', value: '_configure', width: 200, sorter: false,
      render: (_, record) => (
        <Space size={2}>
          <Button size="small" type="link" onClick={() => openConfig(record)}>Configure</Button>
          <Button
            size="small"
            type="link"
            icon={<LockOutlined />}
            onClick={() => openConfig(record, 'permissions')}
          >
            Permissions
          </Button>
        </Space>
      ),
    },
  ];

  const selectedName = selectedUser ? getUserDisplayName(selectedUser) : '';

  return (
    <>
      <AppCard icon={<TeamOutlined />} title="Users"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Users"
          dataSource={users}
          fields={fields}
          loading={loading}
          total={total}
          current={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          onSearch={(q) => { setSearchQuery(q); setPage(1); }}
        />
      </AppCard>

      <Modal
        className="uss-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined style={{ color: 'var(--color-brand)' }} />
            <span>Configure: {selectedName}</span>
          </div>
        }
        width={configType === 'permissions' ? 860 : 780} footer={null} destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div className="uss-config-toggle">
            {CONFIG_TYPES.map((ct) => (
              <button key={ct.key}
                className={`uss-config-btn${configType === ct.key ? ' active' : ''}`}
                onClick={() => setConfigType(ct.key)}>{ct.label}</button>
            ))}
          </div>
        </div>
        {selectedUser && configType === 'permissions' ? (
          <UserPermPanel
            key={String(getUserId(selectedUser) ?? '')}
            user={selectedUser}
          />
        ) : selectedUser && (
          <ModuleFieldConfig
            key={`${String(getUserId(selectedUser) ?? '')}-${configType}`}
            user={selectedUser}
            configType={configType}
          />
        )}
      </Modal>
    </>
  );
}
